import React from "react";
import { Box, Typography, Avatar, useTheme } from "@mui/material";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import AppBoxComponent from "../../mui/AppBoxComponent";
import { getOrderStatus } from "utils/OrderStatus";

const FinFactorBankCard = ({
  logo,
  bankName,
  accountNumber,
  nomineeStatus,
  balance,
  lastUpdated,
  onClick,
  mainCard = false,
  backgroundColor = "#12172A",
  fallbackIcon,
  showChevron = true,
  logoSx = {},
  ...props
}) => {
  const theme = useTheme();
  const status = getOrderStatus(nomineeStatus, theme);

  const cardStyles = {
    display: "flex",
    alignItems: "flex-start", // Better for stacked content on mobile
    p: { xs: 1.75, md: 1.5 },
    backgroundColor: backgroundColor,
    borderRadius: "12px",
    mb: 1.5,
    cursor: onClick ? "pointer" : "default",
    "&:hover": {
      backgroundColor: onClick ? "rgba(255, 255, 255, 0.05)" : backgroundColor,
    },
    ...props.sx,
  };

  return (
    <AppBoxComponent sx={cardStyles} onClick={onClick}>
      <Avatar
        src={logo}
        variant="rounded"
        sx={{
          width: 44,
          height: 44,
          backgroundColor: "white",
          color: "#1B1F3B",
          fontWeight: 700,
          mr: { xs: 1.5, md: 2 },
          p: 0.5,
          objectFit: "contain",
          borderRadius: "8px",
          ...logoSx,
        }}
      >
        {fallbackIcon ||
          (typeof bankName === "string" ? bankName.charAt(0) :
            typeof accountNumber === "string" ? accountNumber.charAt(0) : "")}
      </Avatar>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "flex-start",
          gap: { xs: bankName ? 1.25 : 0.5, md: 4 },
        }}
      >
        {bankName ? (
          <>
            {/* Section 1: Bank Name (Bank UI Mode) */}
            <Box sx={{ flexShrink: 0, width: { xs: "100%", md: "250px" }, mr: { md: 1 } }}>
              <Typography
                variant="body1"
                fontWeight="700"
                color="white"
                sx={{ fontSize: { xs: "1.05rem", md: "1.1rem" } }}
              >
                {bankName}
              </Typography>
            </Box>

            {/* Vertical Divider */}
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                borderLeft: "1px solid rgba(255, 255, 255, 0.1)",
                height: "30px",
                mx: { xs: 0, md: 1 },
              }}
            />

            {/* Section 2: Account Number */}
            <Box sx={{ flexShrink: 0, width: { xs: "100%", md: "180px" }, mt: { xs: 0, md: 0 } }}>
              <Typography
                variant="body2"
                color="rgba(255, 255, 255, 0.5)"
                sx={{ fontSize: { xs: "0.75rem", md: "0.95rem" }, display: "flex", alignItems: "center", gap: 1 }}
              >
                Acc No:
                <Typography
                  component="span"
                  variant="body2"
                  color="rgba(255, 255, 255, 0.9)"
                  fontWeight="500"
                  sx={{ fontSize: { xs: "0.8rem", md: "0.95rem" } }}
                >
                  {accountNumber}
                </Typography>
              </Typography>
            </Box>

            {/* Section 3: Nominee */}
            <Box sx={{ flexShrink: 0, width: { xs: "100%", md: "180px" }, ml: { md: 1 }, mt: { xs: 0, md: 0 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  color="rgba(255, 255, 255, 0.5)"
                  sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                >
                  Nominee:
                </Typography>
                <Typography
                  component="span"
                  variant="body2"
                  color="rgba(255, 255, 255, 0.9)"
                  fontWeight="500"
                  sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                >
                  {status.text}
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          /* Original Simple Layout (Used by BrokerListingContainer) */
          <>
            <Box sx={{ width: { xs: "100%", md: "250px" }, flexShrink: 0, mr: { md: 1 } }}>
              {React.isValidElement(accountNumber) ? (
                accountNumber
              ) : (
                <Typography
                  variant="body1"
                  fontWeight="500"
                  color="white"
                  sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
                >
                  {accountNumber}
                </Typography>
              )}
            </Box>

            {nomineeStatus !== "none" && (
              <>
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    borderLeft: "1px solid rgba(255, 255, 255, 0.1)",
                    height: "30px",
                    mx: { xs: 0, md: 1 },
                  }}
                />

                <Box sx={{ width: { xs: "100%", md: "180px" }, ml: { md: 1 }, flexShrink: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                      variant="body2"
                      color="rgba(255, 255, 255, 0.5)"
                      sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                    >
                      Nominee:
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="rgba(255, 255, 255, 0.9)"
                      fontWeight="500"
                      sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                    >
                      {status.text}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}

        {/* Section 4: Balance (Common to both layouts) */}
        <Box
          sx={{
            textAlign: { xs: "left", md: "right" },
            width: { xs: "100%", md: "auto" },
            flex: 1,
            pr: { xs: 0, md: bankName ? 2 : 0 },
            mt: { xs: bankName ? 1.5 : 1, md: 0 },
            backgroundColor: { xs: "rgba(255, 255, 255, 0.03)", md: "transparent" },
            p: { xs: bankName ? 1.25 : 1, md: 0 },
            borderRadius: { xs: "10px", md: 0 },
            border: { xs: "1px solid rgba(255, 255, 255, 0.05)", md: "none" }
          }}
        >
          {React.isValidElement(balance) ? (
            balance
          ) : (
            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "row", md: "column" }, 
              justifyContent: "space-between", 
              alignItems: { xs: "center", md: "flex-end" } 
            }}>
              <Typography
                variant="body2"
                color="rgba(255, 255, 255, 0.5)"
                sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
              >
                Current Balance
              </Typography>
              <Typography
                variant="body1"
                color="white"
                fontWeight="700"
                sx={{ 
                  fontSize: { xs: bankName ? "1rem" : "0.9rem", md: "1.1rem" },
                  mt: { xs: 0, md: 0.5 } 
                }}
              >
                {balance}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {showChevron && (
        <ChevronRightOutlinedIcon sx={{ color: "rgba(255, 255, 255, 0.4)" }} />
      )}
    </AppBoxComponent>
  );
};

export default FinFactorBankCard;
